import type { Position } from '@/lib/types';
import { DEMO_ALERTS, DEMO_POSITIONS, DEMO_WATCHLIST, PORTFOLIO_CASH } from '@/mocks/market';
import { apiRequest } from '@/lib/api';

// Account surface shared by the demo build and the live BFF.
//
// In live mode every call goes to /api/v1 with the HttpOnly session cookie.
// In mock mode the same shapes are persisted in localStorage so the preview
// behaves like a real product. The mock password digest is SHA-256 for demo
// purposes only; the Go service uses bcrypt and never stores a raw password.

export type AlertKind = 'price_above' | 'price_below' | 'change_pct' | 'volume' | 'rsi';
export type AlertChannel = 'email' | 'in_app';

export interface AccountUser {
  id: string;
  email: string;
  name: string;
  role: string;
  emailVerified: boolean;
  createdAt: string;
}

export interface WatchlistItem {
  symbol: string;
  note: string;
  position: number;
  addedAt: string;
}

export interface Watchlist {
  id: string;
  name: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  items: WatchlistItem[];
}

export interface AccountAlert {
  id: string;
  symbol: string;
  kind: AlertKind;
  threshold: number;
  channel: AlertChannel;
  active: boolean;
  lastTriggeredAt?: string | null;
  createdAt: string;
}

export interface AlertEvent {
  id: string;
  alertId: string;
  symbol: string;
  message: string;
  price?: number | null;
  triggeredAt: string;
  deliveredAt?: string | null;
}

export interface AccountPortfolio {
  id: string;
  name: string;
  cash: number;
  positions: Position[];
}

export type ScreenFilters = Record<string, string | number | boolean>;

export interface SavedScreen {
  id: string;
  name: string;
  filters: ScreenFilters;
  createdAt: string;
  updatedAt: string;
}

export interface RegisterInput {
  email: string;
  name: string;
  password: string;
}

export interface LoginInput {
  email: string;
  password: string;
  remember?: boolean;
}

const LIVE = import.meta.env.VITE_DATA_MODE === 'live';
const STORAGE_KEY = 'stockwatcher.account.v1';

// Preview-mode bootstrap admins. Mirrors the BFF's ADMIN_EMAILS: matching
// accounts are elevated on register and on login. The live cutover uses the
// server list instead; this only affects the browser-local demo store.
const PREVIEW_ADMINS = ((import.meta.env.VITE_ADMIN_EMAILS as string | undefined) ?? '')
  .split(',')
  .map((email) => email.trim().toLowerCase())
  .filter(Boolean);

function previewRole(email: string): string {
  return PREVIEW_ADMINS.includes(email.trim().toLowerCase()) ? 'admin' : 'user';
}

interface StoredUser extends AccountUser {
  passwordHash: string;
}

interface UserData {
  watchlists: Watchlist[];
  alerts: AccountAlert[];
  alertEvents: AlertEvent[];
  portfolio: AccountPortfolio;
  screens: SavedScreen[];
}

interface Database {
  version: 1;
  users: StoredUser[];
  sessionUserId: string | null;
  data: Record<string, UserData>;
}

function emptyDatabase(): Database {
  return { version: 1, users: [], sessionUserId: null, data: {} };
}

function read(): Database {
  if (typeof localStorage === 'undefined') return emptyDatabase();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyDatabase();
    const parsed = JSON.parse(raw) as Database;
    if (parsed.version !== 1) return emptyDatabase();
    return parsed;
  } catch {
    return emptyDatabase();
  }
}

function write(db: Database): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(db));
}

function now(): string {
  return new Date().toISOString();
}

function id(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

async function digest(value: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const bytes = new TextEncoder().encode(value);
    const hash = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(hash))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }
  // Fallback for non-secure contexts; the demo never leaves this browser.
  return `plain:${value}`;
}

function publicUser(user: StoredUser): AccountUser {
  const { passwordHash: _passwordHash, ...rest } = user;
  return rest;
}

function seedData(): UserData {
  const createdAt = now();
  const watchlistItems: WatchlistItem[] = DEMO_WATCHLIST.map((symbol, index) => ({
    symbol,
    note: '',
    position: index,
    addedAt: createdAt,
  }));
  const watchlists: Watchlist[] = [
    {
      id: 'wl-default',
      name: 'Core holdings',
      isDefault: true,
      createdAt,
      updatedAt: createdAt,
      items: watchlistItems,
    },
    {
      id: 'wl-etfs',
      name: 'ETF shortlist',
      isDefault: false,
      createdAt,
      updatedAt: createdAt,
      items: ['SPY', 'QQQ', 'IWM', 'GLD'].map((symbol, index) => ({
        symbol,
        note: '',
        position: index,
        addedAt: createdAt,
      })),
    },
  ];

  const alerts: AccountAlert[] = DEMO_ALERTS.map((alert) => ({
    id: alert.id,
    symbol: alert.symbol,
    kind: alert.kind,
    threshold: alert.threshold,
    channel: alert.channel,
    active: alert.active,
    lastTriggeredAt: null,
    createdAt: `${alert.createdAt}T14:30:00.000Z`,
  }));

  const alertEvents: AlertEvent[] = [
    {
      id: 'ev-1',
      alertId: 'a1',
      symbol: 'NVDA',
      message: 'Crossed $200.00 to the upside',
      price: 200.12,
      triggeredAt: new Date(Date.now() - 20 * 60 * 60 * 1000).toISOString(),
      deliveredAt: new Date(Date.now() - 20 * 60 * 60 * 1000).toISOString(),
    },
    {
      id: 'ev-2',
      alertId: 'a2',
      symbol: 'AAPL',
      message: 'Daily move exceeded −2.00%',
      price: 231.4,
      triggeredAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      deliveredAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    },
  ];

  return {
    watchlists,
    alerts,
    alertEvents,
    portfolio: {
      id: 'pf-default',
      name: 'Long-term book',
      cash: PORTFOLIO_CASH,
      positions: DEMO_POSITIONS.map((position) => ({ ...position })),
    },
    screens: [
      {
        id: 'sc-quality',
        name: 'My compounders',
        filters: { minScore: 68, maxPe: 45, profitableOnly: true, minYield: 0 },
        createdAt,
        updatedAt: createdAt,
      },
      {
        id: 'sc-yield',
        name: 'Income watch',
        filters: { minYield: 3, profitableOnly: true },
        createdAt,
        updatedAt: createdAt,
      },
      {
        id: 'sc-momentum',
        name: 'Trend riders',
        filters: { rsi: 'overbought', minScore: 55 },
        createdAt,
        updatedAt: createdAt,
      },
      {
        id: 'sc-small-value',
        name: 'Small value hunt',
        filters: { cap: 'small', maxPe: 20, profitableOnly: true },
        createdAt,
        updatedAt: createdAt,
      },
    ],
  };
}

function dataFor(db: Database, userId: string): UserData {
  if (!db.data[userId]) db.data[userId] = seedData();
  return db.data[userId];
}

function sessionUser(db: Database): StoredUser | null {
  if (!db.sessionUserId) return null;
  return db.users.find((user) => user.id === db.sessionUserId) ?? null;
}

function requireSession(db: Database): StoredUser {
  const user = sessionUser(db);
  if (!user) throw new Error('Sign in to continue');
  return user;
}

function findWatchlist(data: UserData, listId: string): Watchlist {
  const list = data.watchlists.find((item) => item.id === listId);
  if (!list) throw new Error('Watchlist not found');
  return list;
}

function normaliseEmail(email: string): string {
  return email.trim().toLowerCase();
}

async function mockRegister(input: RegisterInput): Promise<AccountUser> {
  const email = normaliseEmail(input.email);
  const db = read();
  if (db.users.some((user) => user.email === email)) {
    throw new Error('An account with that email already exists');
  }
  const user: StoredUser = {
    id: id(),
    email,
    name: input.name.trim(),
    role: previewRole(email),
    passwordHash: await digest(input.password),
    emailVerified: false,
    createdAt: now(),
  };
  db.users.push(user);
  db.data[user.id] = seedData();
  db.sessionUserId = user.id;
  write(db);
  return publicUser(user);
}

async function mockLogin(input: LoginInput): Promise<AccountUser> {
  const email = normaliseEmail(input.email);
  const db = read();
  const user = db.users.find((candidate) => candidate.email === email);
  if (!user) throw new Error('Email or password is incorrect');
  const hash = await digest(input.password);
  if (hash !== user.passwordHash) throw new Error('Email or password is incorrect');
  // Elevate bootstrap admins on login too, so accounts created before the
  // preview admin list was configured are promoted on next sign-in.
  user.role = previewRole(email) === 'admin' ? 'admin' : user.role;
  db.sessionUserId = user.id;
  write(db);
  return publicUser(user);
}

function mockSession(): AccountUser | null {
  const user = sessionUser(read());
  return user ? publicUser(user) : null;
}

function mockLogout(): void {
  const db = read();
  db.sessionUserId = null;
  write(db);
}

function mockUpdateProfile(name: string): AccountUser {
  const db = read();
  const user = requireSession(db);
  user.name = name.trim();
  write(db);
  return publicUser(user);
}

async function mockChangePassword(currentPassword: string, newPassword: string): Promise<void> {
  const db = read();
  const user = requireSession(db);
  const currentHash = await digest(currentPassword);
  if (currentHash !== user.passwordHash) throw new Error('Current password is incorrect');
  user.passwordHash = await digest(newPassword);
  write(db);
}

function mockWatchlists(): Watchlist[] {
  const db = read();
  const user = requireSession(db);
  return dataFor(db, user.id).watchlists;
}

function mockCreateWatchlist(name: string): Watchlist {
  const db = read();
  const user = requireSession(db);
  const data = dataFor(db, user.id);
  const createdAt = now();
  const list: Watchlist = {
    id: id(),
    name: name.trim() || 'New watchlist',
    isDefault: data.watchlists.length === 0,
    createdAt,
    updatedAt: createdAt,
    items: [],
  };
  data.watchlists.push(list);
  write(db);
  return list;
}

function mockRenameWatchlist(listId: string, name: string): Watchlist {
  const db = read();
  const user = requireSession(db);
  const list = findWatchlist(dataFor(db, user.id), listId);
  list.name = name.trim() || list.name;
  list.updatedAt = now();
  write(db);
  return list;
}

function mockDeleteWatchlist(listId: string): void {
  const db = read();
  const user = requireSession(db);
  const data = dataFor(db, user.id);
  const list = findWatchlist(data, listId);
  if (list.isDefault) throw new Error('The default watchlist cannot be deleted');
  data.watchlists = data.watchlists.filter((item) => item.id !== listId);
  write(db);
}

function mockAddWatchlistItem(listId: string, symbol: string, note = ''): Watchlist {
  const db = read();
  const user = requireSession(db);
  const list = findWatchlist(dataFor(db, user.id), listId);
  const ticker = symbol.trim().toUpperCase();
  if (list.items.some((item) => item.symbol === ticker)) {
    throw new Error(`${ticker} is already on ${list.name}`);
  }
  list.items.push({
    symbol: ticker,
    note,
    position: list.items.length,
    addedAt: now(),
  });
  list.updatedAt = now();
  write(db);
  return list;
}

function mockUpdateWatchlistItem(
  listId: string,
  symbol: string,
  patch: { note?: string; position?: number },
): Watchlist {
  const db = read();
  const user = requireSession(db);
  const list = findWatchlist(dataFor(db, user.id), listId);
  const item = list.items.find((candidate) => candidate.symbol === symbol);
  if (!item) throw new Error(`${symbol} is not on this list`);
  if (patch.note !== undefined) item.note = patch.note;
  if (patch.position !== undefined) item.position = patch.position;
  list.items.sort((a, b) => a.position - b.position);
  list.updatedAt = now();
  write(db);
  return list;
}

function mockRemoveWatchlistItem(listId: string, symbol: string): Watchlist {
  const db = read();
  const user = requireSession(db);
  const list = findWatchlist(dataFor(db, user.id), listId);
  list.items = list.items.filter((item) => item.symbol !== symbol);
  list.updatedAt = now();
  write(db);
  return list;
}

function mockReorderWatchlist(listId: string, symbols: string[]): Watchlist {
  const db = read();
  const user = requireSession(db);
  const list = findWatchlist(dataFor(db, user.id), listId);
  const order = new Map(symbols.map((symbol, index) => [symbol, index]));
  list.items.forEach((item) => {
    const position = order.get(item.symbol);
    if (position !== undefined) item.position = position;
  });
  list.items.sort((a, b) => a.position - b.position);
  list.updatedAt = now();
  write(db);
  return list;
}

function mockAlerts(): { alerts: AccountAlert[]; events: AlertEvent[] } {
  const db = read();
  const user = requireSession(db);
  const data = dataFor(db, user.id);
  return { alerts: data.alerts, events: data.alertEvents };
}

function mockCreateAlert(input: {
  symbol: string;
  kind: AlertKind;
  threshold: number;
  channel: AlertChannel;
}): AccountAlert {
  const db = read();
  const user = requireSession(db);
  const data = dataFor(db, user.id);
  const alert: AccountAlert = {
    id: id(),
    symbol: input.symbol.trim().toUpperCase(),
    kind: input.kind,
    threshold: input.threshold,
    channel: input.channel,
    active: true,
    lastTriggeredAt: null,
    createdAt: now(),
  };
  data.alerts.unshift(alert);
  write(db);
  return alert;
}

function mockUpdateAlert(
  alertId: string,
  patch: Partial<Pick<AccountAlert, 'threshold' | 'channel' | 'active'>>,
): AccountAlert {
  const db = read();
  const user = requireSession(db);
  const data = dataFor(db, user.id);
  const alert = data.alerts.find((item) => item.id === alertId);
  if (!alert) throw new Error('Alert not found');
  Object.assign(alert, patch);
  write(db);
  return alert;
}

function mockDeleteAlert(alertId: string): void {
  const db = read();
  const user = requireSession(db);
  const data = dataFor(db, user.id);
  data.alerts = data.alerts.filter((item) => item.id !== alertId);
  data.alertEvents = data.alertEvents.filter((item) => item.alertId !== alertId);
  write(db);
}

function mockPortfolio(): AccountPortfolio {
  const db = read();
  const user = requireSession(db);
  return dataFor(db, user.id).portfolio;
}

function mockUpdatePortfolio(patch: { name?: string; cash?: number }): AccountPortfolio {
  const db = read();
  const user = requireSession(db);
  const portfolio = dataFor(db, user.id).portfolio;
  if (patch.name !== undefined) portfolio.name = patch.name.trim() || portfolio.name;
  if (patch.cash !== undefined) portfolio.cash = Math.max(0, patch.cash);
  write(db);
  return portfolio;
}

function mockUpsertPosition(symbol: string, shares: number, averageCost: number): AccountPortfolio {
  const db = read();
  const user = requireSession(db);
  const portfolio = dataFor(db, user.id).portfolio;
  const ticker = symbol.trim().toUpperCase();
  const existing = portfolio.positions.find((position) => position.symbol === ticker);
  if (shares <= 0) {
    portfolio.positions = portfolio.positions.filter((position) => position.symbol !== ticker);
  } else if (existing) {
    existing.shares = shares;
    existing.averageCost = averageCost;
  } else {
    portfolio.positions.push({ symbol: ticker, shares, averageCost });
  }
  write(db);
  return portfolio;
}

function mockRemovePosition(symbol: string): AccountPortfolio {
  const db = read();
  const user = requireSession(db);
  const portfolio = dataFor(db, user.id).portfolio;
  portfolio.positions = portfolio.positions.filter((position) => position.symbol !== symbol);
  write(db);
  return portfolio;
}

function mockScreens(): SavedScreen[] {
  const db = read();
  const user = requireSession(db);
  return dataFor(db, user.id).screens;
}

function mockCreateScreen(name: string, filters: ScreenFilters): SavedScreen {
  const db = read();
  const user = requireSession(db);
  const data = dataFor(db, user.id);
  const createdAt = now();
  const screen: SavedScreen = {
    id: id(),
    name: name.trim() || 'Untitled screen',
    filters,
    createdAt,
    updatedAt: createdAt,
  };
  data.screens.push(screen);
  write(db);
  return screen;
}

function mockUpdateScreen(
  screenId: string,
  patch: { name?: string; filters?: ScreenFilters },
): SavedScreen {
  const db = read();
  const user = requireSession(db);
  const data = dataFor(db, user.id);
  const screen = data.screens.find((item) => item.id === screenId);
  if (!screen) throw new Error('Saved screen not found');
  if (patch.name !== undefined) screen.name = patch.name.trim() || screen.name;
  if (patch.filters !== undefined) screen.filters = patch.filters;
  screen.updatedAt = now();
  write(db);
  return screen;
}

function mockDeleteScreen(screenId: string): void {
  const db = read();
  const user = requireSession(db);
  const data = dataFor(db, user.id);
  data.screens = data.screens.filter((item) => item.id !== screenId);
  write(db);
}

async function envelope<T>(path: string, options?: { method?: string; body?: unknown }): Promise<T> {
  const response = await apiRequest<{ data: T }>(path, options);
  return response.data;
}

export const accountApi = {
  isLive: LIVE,

  async session(): Promise<AccountUser | null> {
    if (!LIVE) return mockSession();
    try {
      return await envelope<AccountUser>('/api/v1/auth/me');
    } catch (error) {
      if (error instanceof Error && 'status' in error && (error as { status?: number }).status === 401) {
        return null;
      }
      throw error;
    }
  },

  async register(input: RegisterInput): Promise<AccountUser> {
    if (!LIVE) return mockRegister(input);
    return envelope<AccountUser>('/api/v1/auth/register', { method: 'POST', body: input });
  },

  async login(input: LoginInput): Promise<AccountUser> {
    if (!LIVE) return mockLogin(input);
    return envelope<AccountUser>('/api/v1/auth/login', { method: 'POST', body: input });
  },

  async logout(): Promise<void> {
    if (!LIVE) {
      mockLogout();
      return;
    }
    await apiRequest('/api/v1/auth/logout', { method: 'POST' });
  },

  async updateProfile(name: string): Promise<AccountUser> {
    if (!LIVE) return mockUpdateProfile(name);
    return envelope<AccountUser>('/api/v1/auth/profile', { method: 'PATCH', body: { name } });
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    if (!LIVE) {
      await mockChangePassword(currentPassword, newPassword);
      return;
    }
    await apiRequest('/api/v1/auth/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    });
  },

  async resendVerification(): Promise<void> {
    if (!LIVE) return;
    await apiRequest('/api/v1/auth/verification', { method: 'POST' });
  },

  async watchlists(): Promise<Watchlist[]> {
    if (!LIVE) return mockWatchlists();
    return envelope<Watchlist[]>('/api/v1/watchlists');
  },

  async createWatchlist(name: string): Promise<Watchlist> {
    if (!LIVE) return mockCreateWatchlist(name);
    return envelope<Watchlist>('/api/v1/watchlists', { method: 'POST', body: { name } });
  },

  async renameWatchlist(listId: string, name: string): Promise<Watchlist> {
    if (!LIVE) return mockRenameWatchlist(listId, name);
    return envelope<Watchlist>(`/api/v1/watchlists/${listId}`, { method: 'PATCH', body: { name } });
  },

  async deleteWatchlist(listId: string): Promise<void> {
    if (!LIVE) {
      mockDeleteWatchlist(listId);
      return;
    }
    await apiRequest(`/api/v1/watchlists/${listId}`, { method: 'DELETE' });
  },

  async addWatchlistItem(listId: string, symbol: string, note = ''): Promise<Watchlist> {
    if (!LIVE) return mockAddWatchlistItem(listId, symbol, note);
    return envelope<Watchlist>(`/api/v1/watchlists/${listId}/items`, {
      method: 'POST',
      body: { symbol, note },
    });
  },

  async updateWatchlistItem(
    listId: string,
    symbol: string,
    patch: { note?: string; position?: number },
  ): Promise<Watchlist> {
    if (!LIVE) return mockUpdateWatchlistItem(listId, symbol, patch);
    return envelope<Watchlist>(
      `/api/v1/watchlists/${listId}/items/${encodeURIComponent(symbol)}`,
      { method: 'PATCH', body: patch },
    );
  },

  async removeWatchlistItem(listId: string, symbol: string): Promise<Watchlist> {
    if (!LIVE) return mockRemoveWatchlistItem(listId, symbol);
    return envelope<Watchlist>(
      `/api/v1/watchlists/${listId}/items/${encodeURIComponent(symbol)}`,
      { method: 'DELETE' },
    );
  },

  async reorderWatchlist(listId: string, symbols: string[]): Promise<Watchlist> {
    if (!LIVE) return mockReorderWatchlist(listId, symbols);
    return envelope<Watchlist>(`/api/v1/watchlists/${listId}/order`, {
      method: 'PUT',
      body: { symbols },
    });
  },

  async alerts(): Promise<{ alerts: AccountAlert[]; events: AlertEvent[] }> {
    if (!LIVE) return mockAlerts();
    return envelope<{ alerts: AccountAlert[]; events: AlertEvent[] }>('/api/v1/alerts');
  },

  async createAlert(input: {
    symbol: string;
    kind: AlertKind;
    threshold: number;
    channel: AlertChannel;
  }): Promise<AccountAlert> {
    if (!LIVE) return mockCreateAlert(input);
    return envelope<AccountAlert>('/api/v1/alerts', { method: 'POST', body: input });
  },

  async updateAlert(
    alertId: string,
    patch: Partial<Pick<AccountAlert, 'threshold' | 'channel' | 'active'>>,
  ): Promise<AccountAlert> {
    if (!LIVE) return mockUpdateAlert(alertId, patch);
    return envelope<AccountAlert>(`/api/v1/alerts/${alertId}`, { method: 'PATCH', body: patch });
  },

  async deleteAlert(alertId: string): Promise<void> {
    if (!LIVE) {
      mockDeleteAlert(alertId);
      return;
    }
    await apiRequest(`/api/v1/alerts/${alertId}`, { method: 'DELETE' });
  },

  async portfolio(): Promise<AccountPortfolio> {
    if (!LIVE) return mockPortfolio();
    return envelope<AccountPortfolio>('/api/v1/portfolio');
  },

  async updatePortfolio(patch: { name?: string; cash?: number }): Promise<AccountPortfolio> {
    if (!LIVE) return mockUpdatePortfolio(patch);
    return envelope<AccountPortfolio>('/api/v1/portfolio', { method: 'PATCH', body: patch });
  },

  async upsertPosition(symbol: string, shares: number, averageCost: number): Promise<AccountPortfolio> {
    if (!LIVE) return mockUpsertPosition(symbol, shares, averageCost);
    return envelope<AccountPortfolio>(
      `/api/v1/portfolio/positions/${encodeURIComponent(symbol)}`,
      { method: 'PUT', body: { shares, averageCost } },
    );
  },

  async removePosition(symbol: string): Promise<AccountPortfolio> {
    if (!LIVE) return mockRemovePosition(symbol);
    return envelope<AccountPortfolio>(
      `/api/v1/portfolio/positions/${encodeURIComponent(symbol)}`,
      { method: 'DELETE' },
    );
  },

  async screens(): Promise<SavedScreen[]> {
    if (!LIVE) return mockScreens();
    return envelope<SavedScreen[]>('/api/v1/screens');
  },

  async createScreen(name: string, filters: ScreenFilters): Promise<SavedScreen> {
    if (!LIVE) return mockCreateScreen(name, filters);
    return envelope<SavedScreen>('/api/v1/screens', { method: 'POST', body: { name, filters } });
  },

  async updateScreen(
    screenId: string,
    patch: { name?: string; filters?: ScreenFilters },
  ): Promise<SavedScreen> {
    if (!LIVE) return mockUpdateScreen(screenId, patch);
    return envelope<SavedScreen>(`/api/v1/screens/${screenId}`, { method: 'PATCH', body: patch });
  },

  async deleteScreen(screenId: string): Promise<void> {
    if (!LIVE) {
      mockDeleteScreen(screenId);
      return;
    }
    await apiRequest(`/api/v1/screens/${screenId}`, { method: 'DELETE' });
  },
};

// Demo helpers used by the signed-out pages.
export const demoAccount = {
  watchlist: DEMO_WATCHLIST,
  positions: DEMO_POSITIONS,
  cash: PORTFOLIO_CASH,
  alerts: DEMO_ALERTS,
};
